"use client";

import { useEffect, useState, type RefObject } from "react";
import { useInView, useReducedMotion } from "framer-motion";

/**
 * Drives a looping, phase-based animation.
 *
 * `holds[i]` is how long (ms) phase i stays on screen before the next one. The loop restarts at phase 0 after the
 * last phase. It only runs while the element is on screen. Visitors who prefer reduced motion get the last phase,
 * which each animation uses as its fully completed state.
 */
export function useLoopPhase(ref: RefObject<Element | null>, holds: number[]) {
  const reduce = useReducedMotion();
  const inView = useInView(ref, { amount: 0.35 });
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return;
    let i = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setPhase(i);
      timer = setTimeout(tick, holds[i]);
      i = (i + 1) % holds.length;
    };
    timer = setTimeout(tick, 0);
    return () => clearTimeout(timer);
  }, [inView, reduce, holds]);

  return { phase: reduce ? holds.length - 1 : phase, reduce: !!reduce };
}
